import { CheckCircle, ArrowRight, GraduationCap, Lightbulb, Users, Target, TrendingUp, BookOpen, MessageCircle, Clock, Award, Calendar, Phone } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../../components/FAQAccordion'
import CalComModal from '../../components/CalComModal'
import CallbackModal from '../../components/CallbackModal'
import { useLanguage } from '../../contexts/LanguageContext'
import translations from '../../locales/translations'
import { useMarqueeScroll } from '../../hooks/useMarqueeScroll'
import { getImageDimensions } from '../../utils/imageDimensions'

const Mentorluk = () => {
  const { t, language } = useLanguage()
  const [isCalComModalOpen, setIsCalComModalOpen] = useState(false)
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false)
  const marqueeRef = useMarqueeScroll()
  
  const pageContent = useMemo(() => {
    return (translations as any)[language].servicePages.mentorluk
  }, [language])

  const features = useMemo(() => {
    return pageContent.features.map((feature: any, index: number) => ({
      icon: [GraduationCap, Lightbulb, Target, BookOpen, MessageCircle, TrendingUp][index],
      title: feature.title,
      description: feature.description
    }))
  }, [pageContent])

  const services = useMemo(() => pageContent.services, [pageContent])

  const benefits = useMemo(() => {
    const bulletTr = ['Kişiselleştirilmiş strateji', 'Uzman rehberliği', 'Sürekli destek']
    const bulletEn = ['Personalized strategy', 'Expert guidance', 'Continuous support']
    const bulletRu = ['Персонализированная стратегия', 'Экспертное руководство', 'Постоянная поддержка']
    const bullets = language === 'tr' ? bulletTr : language === 'en' ? bulletEn : bulletRu
    return pageContent.keyBenefits.benefits.map((benefit: any) => ({
      title: benefit.title,
      description: benefit.description,
      benefits: bullets
    }))
  }, [pageContent, language])

  const seoData = useMemo(() => {
    const title = language === 'tr' 
      ? `${pageContent.title} | Dijital Pazarlama Mentörlüğü - Thinklyze`
      : `${pageContent.title} | Digital Marketing Mentorship - Thinklyze`
    
    const description = language === 'tr'
      ? `${pageContent.description} Dijital pazarlama alanında uzman mentörlük hizmetleri. Birebir danışmanlık ve eğitim. İstanbul, Türkiye.`
      : `${pageContent.description} Expert mentorship services in digital marketing. One-on-one consulting and training. Istanbul, Turkey.`
    
    const keywords = language === 'tr'
      ? 'Dijital pazarlama mentörlüğü, pazarlama danışmanlığı, dijital pazarlama eğitimi, birebir mentörlük, iş geliştirme, İstanbul, Türkiye'
      : 'Digital marketing mentorship, marketing consulting, digital marketing training, one-on-one mentorship, business development, Istanbul, Turkey'
    
    return { title, description, keywords }
  }, [pageContent, language])

  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Digital Marketing Mentorship",
    "provider": {
      "@type": "Organization",
      "name": "Thinklyze",
      "url": "https://thinklyze.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Turkey"
    },
    "description": seoData.description,
    "name": seoData.title,
    "url": "https://thinklyze.com/services/mentorluk"
  }), [seoData])

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href="https://thinklyze.com/services/mentorluk" />
        <link rel="alternate" hreflang="tr" href="https://thinklyze.com/services/mentorluk" />
        <link rel="alternate" hreflang="en" href="https://thinklyze.com/services/mentorluk?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://thinklyze.com/services/mentorluk" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content="https://thinklyze.com/services/mentorluk" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={language === 'tr' ? 'en_US' : 'tr_TR'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    <div className="pt-16 bg-[#f9f9f9] relative">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
      
      {/* Hero Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.05)] p-8 md:p-16">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              {/* Left Column - Text Content */}
              <div className="flex-1 space-y-6">
                <span className="inline-block text-[13px] font-medium text-[#666] bg-[#f3f3f3] px-3 py-1 rounded-full">
                  {pageContent.tag}
                </span>
                
                <h1 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1]">
                  {pageContent.title}
                </h1>
                
                <p className="text-lg text-[#555] max-w-[500px] leading-relaxed">
                  {pageContent.description}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => setIsCalComModalOpen(true)}
                    className="inline-flex items-center justify-center bg-black text-white px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
                  >
                    {t('servicePages.common.scheduleMeeting')}
                    <Calendar className="w-4 h-4 ml-2" />
                  </button>
                  <button
                    onClick={() => setIsCallbackModalOpen(true)}
                    className="inline-flex items-center justify-center bg-transparent text-gray-700 border border-gray-300 px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    {t('servicePages.common.callMe')}
                    <Phone className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>

              {/* Right Column - Mockup Card */}
              <div className="flex-1 max-w-[500px] w-full">
                <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                  {/* Mockup Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#111] text-base">
                        {language === 'tr' ? 'Mentörlük Programı' : language === 'en' ? 'Mentorship Program' : 'Программа менторства'}
                      </div>
                      <div className="text-sm text-[#666]">
                        {language === 'tr' ? 'Kişisel Gelişim ve Strateji' : language === 'en' ? 'Personal Development and Strategy' : 'Личное развитие и стратегия'}
                      </div>
                    </div>
                  </div>

                  {/* Mockup Title */}
                  <h3 className="font-bold text-[#111] text-lg mb-2">
                    {language === 'tr' ? '1-on-1 Mentörlük Seansı' : language === 'en' ? '1-on-1 Mentorship Session' : 'Индивидуальная сессия менторства'}
                  </h3>
                  <p className="text-sm text-[#666] mb-6">
                    {language === 'tr'
                      ? 'Dijital pazarlama alanında uzman rehberliği ile hedeflerinize ulaşın.'
                      : language === 'en'
                      ? 'Reach your goals with expert guidance in digital marketing.'
                      : 'Достигайте целей с экспертным руководством в цифровом маркетинге.'}
                  </p>

                  {/* Mockup Details */}
                  <div className="space-y-3 mb-6 text-sm text-[#555]">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{language === 'tr' ? '60-90 dakika' : language === 'en' ? '60-90 minutes' : '60–90 минут'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>{language === 'tr' ? 'Birebir Görüşme' : language === 'en' ? 'One-on-One Meeting' : 'Индивидуальная встреча'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      <span>{language === 'tr' ? 'Online / Yüz Yüze' : language === 'en' ? 'Online / Face-to-Face' : 'Онлайн / Лично'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      <span>{language === 'tr' ? 'Sertifika Desteği' : language === 'en' ? 'Certificate Support' : 'Поддержка по сертификации'}</span>
                    </div>
                  </div>

                  {/* Mockup Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">50+</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Mentör' : language === 'en' ? 'Mentors' : 'Менторов'}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">500+</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Öğrenci' : language === 'en' ? 'Students' : 'Участников'}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">95%</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Memnuniyet' : language === 'en' ? 'Satisfaction' : 'Довольны'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="relative py-8 border-t border-gray-200 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Client Logos Marquee - Single Row */}
          <div ref={marqueeRef} className="w-full overflow-hidden md:overflow-hidden overflow-x-auto md:overflow-x-hidden py-4 relative marquee-container-mobile">
            {/* Fade gradients - hidden on mobile */}
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="flex animate-marquee-right gap-4 md:gap-4">
              {/* First set */}
              {[
                '/images/brands/ref1.webp',
                '/images/brands/ref2.webp',
                '/images/brands/ref3.webp',
                '/images/brands/ref4.webp',
                '/images/brands/ref6.webp',
                '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
                '/images/brands/veela1.webp',
                '/images/brands/daphnesandals.webp',
              ].map((logo, index) => {
                const dimensions = getImageDimensions(logo)
                return (
                  <div key={`logo-${index}`} className="marquee-item-mobile flex-shrink-0 bg-white rounded-xl p-3 md:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <img 
                      src={logo} 
                      alt={`Referans ${index + 1}`} 
                      loading="lazy"
                      decoding="async"
                      width={dimensions?.width}
                      height={dimensions?.height}
                      className="h-10 md:h-12 max-w-[120px] md:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
              {/* Duplicate set for seamless loop - hidden on mobile */}
              {[
                '/images/brands/ref1.webp',
                '/images/brands/ref2.webp',
                '/images/brands/ref3.webp',
                '/images/brands/ref4.webp',
                '/images/brands/ref6.webp',
                '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
                '/images/brands/veela1.webp',
                '/images/brands/daphnesandals.webp',
              ].map((logo, index) => {
                const dimensions = getImageDimensions(logo)
                return (
                  <div key={`logo-dup-${index}`} className="hidden md:flex flex-shrink-0 bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <img 
                      src={logo} 
                      alt={`Referans ${index + 1}`} 
                      loading="lazy"
                      decoding="async"
                      width={dimensions?.width}
                      height={dimensions?.height}
                      className="h-12 max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Title Below Logos */}
          <div className="text-center mt-4">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-50 rounded-full border border-gray-100">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <p className="text-sm md:text-base text-gray-700 font-medium font-jakarta">
                {t('servicePages.common.trustedBy')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm text-[#666] mb-3 font-medium">{t('servicePages.common.keyBenefits')}</p>
            <h2 className="text-[clamp(28px,4vw,42px)] font-bold text-[#111] leading-[1.2] mb-4 md:mb-5">
              {pageContent.keyBenefits.title}
            </h2>
            <p className="text-base md:text-lg text-[#555] max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              {pageContent.keyBenefits.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10 mt-12 md:mt-16">
            {benefits.map((benefit: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                {/* Number Badge */}
                <div className="absolute -top-4 left-6 md:left-8">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg md:text-xl">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-6 md:pt-8">
                  <h3 className="text-xl md:text-2xl font-bold text-[#111] mb-3 md:mb-4 leading-tight group-hover:text-blue-600 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-sm md:text-base text-[#555] leading-relaxed mb-4">
                    {benefit.description}
                  </p>
                  <ul className="space-y-2.5">
                    {benefit.benefits.map((item: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2.5 text-[#555]">
                        <CheckCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Decorative Line */}
                <div className="absolute bottom-0 left-6 md:left-8 right-6 md:right-8 h-1 bg-gradient-to-r from-blue-500 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm text-[#666] mb-3 font-medium">{t('servicePages.common.additionalFeatures')}</p>
            <h2 className="text-[clamp(28px,4vw,42px)] font-bold text-[#111] leading-[1.2] mb-4 md:mb-5">
              {language === 'tr' ? 'Hangi konularda mentörlük alabilirsiniz?' : language === 'en' ? 'What topics can you get mentorship on?' : 'По каким темам доступно менторство?'}
            </h2>
            <p className="text-base md:text-lg text-[#555] max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              {pageContent.additionalFeatures.subtitle}
            </p>
          </div>

          {/* Services List - Clean Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {services.map((service: string, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 p-4 bg-[#f9f9f9] rounded-lg hover:bg-gray-100 transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <span className="text-[#555] font-medium">{service}</span>
              </motion.div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
            {features.map((feature: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-blue-50 group-hover:to-blue-100 flex items-center justify-center mb-4 md:mb-5 transition-all duration-300">
                  <feature.icon className="w-6 h-6 md:w-7 md:h-7 text-gray-700 group-hover:text-blue-600 transition-colors duration-300" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-[#111] mb-2 md:mb-3 leading-tight group-hover:text-blue-600 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-[#555] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#f9f9f9] py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[clamp(28px,4vw,40px)] font-bold text-[#111] leading-tight mb-6">
              {language === 'tr' ? 'Mentörlük süreci nasıl işler?' : language === 'en' ? 'How does the mentorship process work?' : 'Как работает процесс менторства?'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Target,
                title: language === 'tr' ? 'İhtiyaç Analizi' : language === 'en' ? 'Needs Analysis' : 'Анализ потребностей',
                points: language === 'tr' ? ['Mevcut durum analizi', 'Hedef belirleme', 'Mentör eşleştirme'] : language === 'en' ? ['Current situation analysis', 'Goal setting', 'Mentor matching'] : ['Анализ текущей ситуации', 'Постановка целей', 'Подбор ментора']
              },
              {
                icon: Users,
                title: language === 'tr' ? 'Mentör Eşleştirme' : language === 'en' ? 'Mentor Matching' : 'Подбор ментора',
                points: language === 'tr' ? ['Uzmanlık alanına göre', 'Deneyimli profesyoneller', 'Kişisel uyum'] : language === 'en' ? ['By expertise area', 'Experienced professionals', 'Personal fit'] : ['По области экспертизы', 'Опытные специалисты', 'Личная совместимость']
              },
              {
                icon: BookOpen,
                title: language === 'tr' ? 'Birebir Seanslar' : language === 'en' ? 'One-on-One Sessions' : 'Индивидуальные сессии',
                points: language === 'tr' ? ['Düzenli görüşmeler', 'Strateji geliştirme', 'Uygulama takibi'] : language === 'en' ? ['Regular meetings', 'Strategy development', 'Implementation tracking'] : ['Регулярные встречи', 'Разработка стратегии', 'Контроль реализации']
              },
              {
                icon: TrendingUp,
                title: language === 'tr' ? 'Takip ve Optimizasyon' : language === 'en' ? 'Tracking and Optimization' : 'Отслеживание и оптимизация',
                points: language === 'tr' ? ['Sürekli destek', 'Performans analizi', 'Gelişim takibi'] : language === 'en' ? ['Continuous support', 'Performance analysis', 'Progress tracking'] : ['Постоянная поддержка', 'Анализ эффективности', 'Отслеживание прогресса']
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 border border-gray-100"
              >
                <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mb-4">
                  <step.icon className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-sm font-semibold text-yellow-600 mb-2">{language === 'tr' ? 'Adım' : language === 'en' ? 'Step' : 'Шаг'} {index + 1}</div>
                <h3 className="text-lg font-bold text-[#111] mb-4">{step.title}</h3>
                <ul className="space-y-2">
                  {step.points.map((point: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#555]">
                      <CheckCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQAccordion
        title={pageContent.faq.title}
        subtitle={pageContent.faq.subtitle}
        faqs={pageContent.faq.items}
        onCTAClick={() => setIsCalComModalOpen(true)}
      />

      {/* Modals */}
      <CalComModal 
        isOpen={isCalComModalOpen} 
        onClose={() => setIsCalComModalOpen(false)} 
      />
      <CallbackModal 
        isOpen={isCallbackModalOpen} 
        onClose={() => setIsCallbackModalOpen(false)} 
      />

    </div>
    </>
  )
}

export default Mentorluk















